'use client';

import { useEffect } from 'react';

// First-party error beacon for the telemetry blind spot: browsers where
// PostHog never loads (ad blockers, strict tracking protection, or a crash
// before the SDK initializes). Those users' failures were invisible; the
// Aug 2026 crash hunt had to infer them from absence. This posts a minimal
// same-origin beacon (ad blockers don't block first-party endpoints) that
// the worker relays into error tracking server-side.
//
// Deliberately dormant when PostHog is up: its own exception autocapture
// owns that path, so nothing is double-reported.

const MAX_BEACONS_PER_PAGE = 3;

type PostHogWindow = Window & { posthog?: { __loaded?: boolean } };

export default function ErrorBeacon() {
  useEffect(() => {
    let sent = 0;

    const send = (message: string, stack: string | undefined, kind: string) => {
      const w = window as PostHogWindow;
      if (w.posthog?.__loaded) return; // PostHog is alive; it captures this itself.
      if (sent >= MAX_BEACONS_PER_PAGE) return;
      sent += 1;
      try {
        void fetch('/api/beacon/error', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: String(message || 'unknown error').slice(0, 500),
            stack: stack ? String(stack).slice(0, 1500) : null,
            kind,
            url: window.location.href.slice(0, 500),
          }),
        });
      } catch {
        // The beacon must never create its own error surface.
      }
    };

    const onError = (event: ErrorEvent) => {
      send(event.message, event.error?.stack, 'error');
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as { message?: string; stack?: string } | string | undefined;
      const message = typeof reason === 'string' ? reason : reason?.message || 'unhandled rejection';
      const stack = typeof reason === 'object' ? reason?.stack : undefined;
      send(message, stack, 'unhandledrejection');
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
